import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Plus, Search, Loader2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Card, CardContent } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { toast } from '@/app/components/ui/simple-toaster';
import { useCFLocations, useCreateCFLocation } from '@/hooks/use-cf-locations';
import { useLocationTemplates, useApplyTemplate } from '@/hooks/use-location-templates';
import { useIndustry } from '@/hooks/use-industry';
import { handleApiError } from '@/lib/api-client';
import type { CFLocationType, Industry, LocationTemplate } from '@/lib/types';

const LOCATION_TYPE_DEFAULTS: Record<Industry, CFLocationType> = {
  field_ops: 'job_site',
  facility_ops: 'client_facility',
  training_ops: 'training_venue',
  consultant: 'client_facility',
  hybrid: 'job_site',
};

const LOCATION_TYPES: { value: CFLocationType; label: string }[] = [
  { value: 'job_site', label: 'Job Site' },
  { value: 'client_facility', label: 'Client Facility' },
  { value: 'training_venue', label: 'Training Venue' },
  { value: 'depot', label: 'Depot' },
  { value: 'office', label: 'Office' },
  { value: 'client_hq', label: 'Client HQ' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'other', label: 'Other' },
];

export function LocationsListPage() {
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const navigate = useNavigate();
  const { labels } = useIndustry();

  const { data, isLoading } = useCFLocations({ q: search || undefined });
  const locations = data?.results ?? [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <MapPin className="h-7 w-7" />
            {labels.location}s
          </h1>
          <p className="text-gray-600 mt-1">
            {data?.count ?? 0} {labels.location.toLowerCase()}{(data?.count ?? 0) !== 1 ? 's' : ''}
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add {labels.location}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder={`Search ${labels.location.toLowerCase()}s...`}
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && locations.length === 0 && (
        <Card>
          <CardContent className="pt-6 text-center py-12">
            <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900">No {labels.location.toLowerCase()}s found</h3>
            <p className="text-gray-600 mt-1">
              {search
                ? 'Try adjusting your search.'
                : `Add your first ${labels.location.toLowerCase()} to get started.`}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      {!isLoading && locations.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>City</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Active</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locations.map((loc) => {
                const typeLabel =
                  LOCATION_TYPES.find((t) => t.value === loc.type)?.label ?? loc.type;
                return (
                  <TableRow
                    key={loc.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/locations/${loc.id}`)}
                  >
                    <TableCell className="font-medium">{loc.name}</TableCell>
                    <TableCell>{typeLabel}</TableCell>
                    <TableCell>{loc.city || '—'}</TableCell>
                    <TableCell>{loc.state_province || '—'}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          loc.is_active
                            ? 'bg-green-50 text-green-700'
                            : 'bg-red-50 text-red-700'
                        }
                      >
                        {loc.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <AddLocationDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}

/** Template metadata mapped to parameter input labels. */
const TEMPLATE_PARAM_LABELS: Record<string, string> = {
  floors: 'Number of floors',
  units: 'Number of units',
};

function AddLocationDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { labels, industry, fieldRelevance } = useIndustry();
  const defaultType: CFLocationType = LOCATION_TYPE_DEFAULTS[industry];
  const navigate = useNavigate();

  const { data: templates } = useLocationTemplates();
  const applyTemplateMutation = useApplyTemplate();

  const [step, setStep] = useState<'template' | 'details'>('template');
  const [selectedTemplate, setSelectedTemplate] = useState<LocationTemplate | null>(null);
  const [templateCounts, setTemplateCounts] = useState<Record<string, number>>({});

  const [name, setName] = useState('');
  const [type, setType] = useState<CFLocationType>(defaultType);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setType(defaultType); }, [defaultType]);
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [country, setCountry] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useCreateCFLocation();

  const resetForm = () => {
    setStep('template');
    setSelectedTemplate(null);
    setTemplateCounts({});
    setName('');
    setType(defaultType);
    setAddress('');
    setCity('');
    setStateProvince('');
    setCountry('');
    setPostalCode('');
    setError(null);
  };

  const handleClose = (val: boolean) => {
    if (!val) resetForm();
    onOpenChange(val);
  };

  const handleTemplateSelect = (tpl: LocationTemplate | null) => {
    setSelectedTemplate(tpl);
    if (tpl && tpl.key !== 'blank') {
      setType(tpl.location_type);
      // Reset counts to only the current template's param
      const paramMap: Record<string, string> = { building: 'floors', industrial: 'units' };
      const pk = paramMap[tpl.key];
      if (pk) {
        setTemplateCounts({ [pk]: templateCounts[pk] ?? 3 });
      } else {
        setTemplateCounts({});
      }
    } else {
      setType(defaultType);
      setTemplateCounts({});
    }
  };

  const handleNext = () => {
    setStep('details');
  };

  const handleBack = () => {
    setError(null);
    setStep('template');
  };

  const handleSubmit = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }

    try {
      const loc = await createMutation.mutateAsync({
        name: name.trim(),
        type,
        address: address.trim() || undefined,
        city: city.trim() || undefined,
        state_province: stateProvince.trim() || undefined,
        country: country.trim() || undefined,
        postal_code: postalCode.trim() || undefined,
      });

      // Apply template if one was selected (not blank)
      if (selectedTemplate && selectedTemplate.key !== 'blank') {
        try {
          const result = await applyTemplateMutation.mutateAsync({
            locationId: loc.id,
            payload: {
              template_key: selectedTemplate.key,
              counts: Object.keys(templateCounts).length > 0 ? templateCounts : undefined,
            },
          });
          toast.success(
            `${loc.name} created with ${result.zones_created} zone(s) and ${result.spots_created} spot(s)`
          );
        } catch (_tplErr) {
          // Location was created but template application failed
          toast.error(`${loc.name} created but template application failed — apply it manually from the detail page.`);
        }
      } else {
        toast.success(`${loc.name} created`);
      }

      handleClose(false);
      navigate(`/locations/${loc.id}`);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    }
  };

  // Determine if this template has parameterized counts
  const getTemplateParamKey = (tpl: LocationTemplate | null): string | null => {
    if (!tpl) return null;
    // Match template key to known parameterized count inputs
    const paramMap: Record<string, string> = {
      building: 'floors',
      industrial: 'units',
    };
    return paramMap[tpl.key] ?? null;
  };

  const paramKey = getTemplateParamKey(selectedTemplate);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {step === 'template' ? 'Start from a template' : `Add ${labels.location}`}
          </DialogTitle>
          <DialogDescription>
            {step === 'template'
              ? 'Choose a template to pre-populate zones and spots, or start blank.'
              : `Enter ${labels.location.toLowerCase()} details.`}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {step === 'template' && (
          <>
            <div className="space-y-3 py-2">
              {(templates ?? []).map((tpl) => (
                <Card
                  key={tpl.key}
                  className={`cursor-pointer transition-colors ${
                    selectedTemplate?.key === tpl.key
                      ? 'border-blue-500 bg-blue-50'
                      : 'hover:border-gray-400'
                  }`}
                  onClick={() => handleTemplateSelect(tpl)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{tpl.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {tpl.key === 'blank'
                            ? 'Start empty — add zones and spots manually.'
                            : `Creates pre-configured zones and spots.`}
                        </p>
                      </div>
                      {selectedTemplate?.key === tpl.key && (
                        <Badge variant="default" className="ml-2">Selected</Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Parameter input for parameterized templates */}
              {paramKey && (
                <div className="space-y-2 pt-2">
                  <Label htmlFor="template-count">
                    {TEMPLATE_PARAM_LABELS[paramKey] ?? paramKey}
                  </Label>
                  <Input
                    id="template-count"
                    type="number"
                    min={1}
                    max={100}
                    value={templateCounts[paramKey] ?? 3}
                    onChange={(e) =>
                      setTemplateCounts((prev) => ({
                        ...prev,
                        [paramKey]: parseInt(e.target.value, 10) || 1,
                      }))
                    }
                  />
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button onClick={handleNext}>
                Next
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'details' && (
          <>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="loc-name">Name *</Label>
                  <Input id="loc-name" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="loc-type">Type</Label>
                  <Select value={type} onValueChange={(v) => setType(v as CFLocationType)}>
                    <SelectTrigger id="loc-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LOCATION_TYPES
                        .filter((t) => t.value !== 'vehicle' || fieldRelevance.uses_vehicles)
                        .map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {fieldRelevance.requires_address && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="loc-address">Address</Label>
                    <Input id="loc-address" value={address} onChange={(e) => setAddress(e.target.value)} />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="loc-city">City</Label>
                      <Input id="loc-city" value={city} onChange={(e) => setCity(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="loc-state">State</Label>
                      <Input id="loc-state" value={stateProvince} onChange={(e) => setStateProvince(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="loc-country">Country</Label>
                      <Input id="loc-country" value={country} onChange={(e) => setCountry(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="loc-postal">Postal</Label>
                      <Input id="loc-postal" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
                    </div>
                  </div>
                </>
              )}

              {selectedTemplate && selectedTemplate.key !== 'blank' && (
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm text-blue-700">
                  Template "{selectedTemplate.label}" will be applied after creation.
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleBack}>
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={createMutation.isPending || applyTemplateMutation.isPending}
              >
                {(createMutation.isPending || applyTemplateMutation.isPending) && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Add {labels.location}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
